import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { client } from "../client";
import BlockContent from "@sanity/block-content-to-react";
import { motion } from 'framer-motion';
import { CursorContext } from '../context/CursorContext';
import imageUrlBuilder from '@sanity/image-url';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

const builder = imageUrlBuilder(client);
function urlFor(source) {
  return builder.image(source);
}

export default function About() {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const theme = useTheme();
  const darkMode = theme?.darkMode ?? true;

  const [author, setAuthor] = useState(null);

  useEffect(() => {
    client.fetch(
      `*[_type == "author"] {
        name,
        bio,
        "authorImage": image.asset -> url,
        "recognition": recognition[] {
          label,
          title,
          description,
          year,
          link,
          "awardImage": awardImage.asset -> url
        }
      }`
    )
    .then((data) => setAuthor(data[0]))
    .catch(console.error);
  }, []);

  if (!author) return null;

  return (
    <main
      style={{
        background: darkMode
          ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
          : '#ffffff',
        color: darkMode ? '#ffffff' : '#0f172a',
        transition: 'all 0.3s ease',
      }}
      className="w-full min-h-screen"
    >

      <section>

        {/* ABOUT SECTION */}
        <div className="max-w-6xl px-8 mx-auto pt-16 md:pt-24 pb-10 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
          >
            {author.authorImage && (
              <img
                src={urlFor(author.authorImage).url()}
                alt={author.name}
                className="w-full max-w-[450px] rounded-lg object-cover"
              />
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            className="flex flex-col lg:w-[35rem]"
          >
            <h1 className={`h1 font-bold mb-5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              About Me
            </h1>

            <div className={`${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              <BlockContent
                blocks={author.bio}
                projectId="lr5lcgfp"
                dataset="production"
              />
            </div>

            <Link
              to="/contact"
              onMouseEnter={mouseEnterHandler}
              onMouseLeave={mouseLeaveHandler}
              className="rounded mb-[30px] py-2 my-10 w-fit px-10 text-white bg-sky-900 cursor-pointer"
            >
              HIRE ME
            </Link>
          </motion.div>
        </div>

        {/* RECOGNITION */}
        <div className="max-w-6xl px-5 mx-auto pb-20 lg:pb-32">

          <h2 className={`h1 font-bold mb-10 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Recognition
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

            {author.recognition?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col border rounded-xl overflow-hidden transition-colors duration-300
                  ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}
              >

                <div className="w-full aspect-[4/3] overflow-hidden">
                  {item.awardImage ? (
                    <img
                      src={urlFor(item.awardImage).width(600).url()}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                      No Image
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 p-5 flex-1">

                  {item.label && (
                    <span className="text-[11px] uppercase text-slate-400 border border-white/10 rounded px-2 py-1 w-fit">
                      {item.label}
                    </span>
                  )}

                  {item.title && (
                    <p className={`${darkMode ? 'text-white' : 'text-slate-900'} font-semibold`}>
                      {item.title}
                    </p>
                  )}

                  {item.description && (
                    <p className="text-sm text-slate-400">
                      {item.description}
                    </p>
                  )}

                  <div className="flex justify-between mt-auto pt-2">
                    <span className="text-sm text-slate-500">
                      {item.year}
                    </span>

                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-sky-400 underline"
                      >
                        View →
                      </a>
                    )}
                  </div>

                </div>
              </motion.div>
            ))}

          </div>
        </div>

        <Footer />

      </section>
    </main>
  );
}